import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { get, isArray, isEmpty } from "lodash";
import { exportFile, formatNumber, showError, showSuccess, unformatNumber } from "../../../../../utils/utilities";
import { useParams } from "react-router-dom";
import { setLoading } from "../../../../../redux/ducks/Loading";
import { Input, Row, Table } from "antd";
import Button from "../../../../../components/functional_components/Button/PFButton";
import { DownloadOutlined, PrinterOutlined, SaveOutlined } from "@ant-design/icons";
import ProjectDashboard from "../../../../../services/api/ProjectDashboard";
import { initializeForm, setValuePerformance } from "../../../../../redux/ducks/EditableTable";
import html2canvas from "html2canvas";

const Performance = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const params = useParams();
  const customForm = useSelector((state) => get(state.EditableTableDuck, "customForm", {}));
  const isDirtyCustomForm = useSelector((state) => get(state.EditableTableDuck, "isDirty", {}));
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const [state, setState] = useState({
    loading: false,
    saveLoading: false,
  });

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    dispatch(setLoading(true));
    let initForm = {};
    const data = await ProjectDashboard.getDataPerformance(params.id);
    if (!data.error) {
      data.data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
        if (cc.type === "REVENUE") {
          initForm["REVENUE"] = {
            type: cc.type,
            amount: cc.upSelling.CHANGE_ORDERS_TO_BE_RECEIVED,
            column: "CHANGE_ORDERS_TO_BE_RECEIVED",
            planned: cc.planned,
          };
        } else if (cc.type === "VALUES") {
          initForm[`VALUES-${i}`] = [];
          initForm[`VALUES-${i}`].push({
            type: cc.type,
            projectTaskId: cc.projectTaskId,
            amount: cc.projectProgressValues.COMMITTED,
            column: "COMMITTED",
            wbsCategoryId: cc.wbsCategoryId,
            planned: cc.planned,
          });
          initForm[`VALUES-${i}`].push({
            type: cc.type,
            projectTaskId: cc.projectTaskId,
            amount: cc.upSelling.CHANGE_ORDERS_TO_BE_RECEIVED,
            column: "CHANGE_ORDERS_TO_BE_RECEIVED",
            wbsCategoryId: cc.wbsCategoryId,
            planned: cc.planned,
          });
        }
        // if (!isEmpty(initForm)) {
        //   cForm.push(initForm);
        // }
      });
      dispatch(initializeForm(params.id, initForm));
      setState((prevState) => ({
        ...prevState,
        tableContent: data.data,
      }));
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const columns = (isPrint = false) => {
    const amountSuffix = isPrint ? "" : "EUR";
    const formatNumberPerformance = (amount, record) => {
      return formatNumber({
        decimalScale:
          record.type === "MARGIN_PERCENT" || record.type === "PROGRESS_PERCENT" || record.type === "PERCENT" ? 1 : 0,
        value: amount,
        lang: currentLanguage,
        suffix:
          record.type === "MARGIN_PERCENT" || record.type === "PROGRESS_PERCENT" || record.type === "PERCENT"
            ? "PERCENT"
            : amountSuffix,
      });
    };

    const editableNumberDecimalScale = (record, isEditable) => {
      if (record.type === "MARGIN_PERCENT" || record.type === "PROGRESS_PERCENT" || record.type === "PERCENT") {
        return 1;
      } else if (isPrint || !isEditable) {
        return 0;
      } else {
        return 2;
      }
    };

    const coloredDot = (amount, record) => {
      let color;
      if (record.type === "VALUES" || record.type === "COSTS") {
        if (amount === 0) {
          color = "yellow";
        } else if (amount > 0) {
          color = "red";
        } else {
          color = "green";
        }
      } else {
        if (amount === 0) {
          color = "yellow";
        } else if (amount < 0) {
          color = "red";
        } else {
          color = "green";
        }
      }

      return (
        <div
          className={"pr-10"}
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: `${color}`,
          }}
        ></div>
      );
    };

    const keySuffix = isPrint ? "_print" : "";
    return [
      {
        title: "",
        dataIndex: "activity",
        width: 200,
        fixed: "left",
        className: "breakline" + (isPrint ? " tablePrintLargeFontSize" : ""),
        render: (activity, record) => {
          let name = "";
          switch (record.type) {
            case "REVENUE":
              name = t("projectsManagement.dashboard.performance.revenue");
              break;
            case "VALUES":
              name = `${record.description}`;
              break;
            case "COSTS":
              name = t("projectsManagement.dashboard.performance.costs");
              break;
            case "MARGIN":
              name = t("projectsManagement.dashboard.performance.margin");
              break;
            case "MARGIN_PERCENT":
              name = t("projectsManagement.dashboard.performance.margin_percent");
              break;
            case "PROGRESS_PERCENT":
              name = t("projectsManagement.dashboard.performance.progress_percent");
              break;
            default:
              name = activity;
              break;
          }
          return name;
        },
      },
      {
        title: t("projectsManagement.dashboard.performance.budget"),
        className: "head" + (isPrint ? " tablePrintLargeFontSize" : ""),
        children: [
          {
            title: t("projectsManagement.dashboard.performance.initialAsSoldBudget"),
            dataIndex: ["budgetValues", "INITIAL_AS_SOLD_BUDGET"],
            width: 150,
            className: "table-value-right toSeeBetterShadow" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) =>
              record.type !== "PROGRESS_PERCENT" && (
                <div className="oneline">{formatNumberPerformance(amount, record)}</div>
              ),
          },
          {
            title: t("projectsManagement.dashboard.performance.initialBaselineBudget"),
            dataIndex: ["budgetValues", "INITIAL_BASELINE_BUDGET"],
            width: 150,
            className: "table-value-right toSeeBetterShadow" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) =>
              record.type !== "PROGRESS_PERCENT" && (
                <div className="oneline">{formatNumberPerformance(amount, record)}</div>
              ),
          },
          {
            title: t("projectsManagement.dashboard.performance.currentAsSoldBudget"),
            dataIndex: ["budgetValues", "CURRENT_AS_SOLD_BUDGET"],
            width: 150,
            className: "table-value-right toSeeBetterShadow" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) =>
              record.type !== "PROGRESS_PERCENT" && (
                <div className="oneline">{formatNumberPerformance(amount, record)}</div>
              ),
          },
          {
            title: t("projectsManagement.dashboard.performance.currentBaselineBudget"),
            dataIndex: ["budgetValues", "CURRENT_BASELINE_BUDGET"],
            width: 150,
            className: "table-value-right toSeeBetterShadow" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) =>
              record.type !== "PROGRESS_PERCENT" && (
                <div className="oneline">{formatNumberPerformance(amount, record)}</div>
              ),
          },
        ],
      },
      {
        title: t("projectsManagement.dashboard.performance.projectProgress"),
        className: isPrint ? "tablePrintLargeFontSize" : "",
        children: [
          {
            title: t("projectsManagement.dashboard.performance.actual"),
            dataIndex: ["projectProgressValues", "ACTUAL"],
            width: 150,
            className: "table-value-right toSeeBetter" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) => <div className="oneline">{formatNumberPerformance(amount, record)}</div>,
          },
          {
            title: t("projectsManagement.dashboard.performance.committed"),
            dataIndex: ["projectProgressValues", "COMMITTED"],
            width: 150,
            className: "table-value-right toSeeBetter" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) =>
              isPrint ? (
                <div className="oneline">{formatNumberPerformance(amount, record)}</div>
              ) : record.planned ? (
                <div className="oneline">{formatNumberPerformance(amount, record)}</div>
              ) : (
                formatNumber({
                  decimalScale: editableNumberDecimalScale(
                    record,
                    userInfo.canEditDashboard && projectManagement.enabled && record.type === "VALUES"
                  ),
                  name: `committed_${record.key}${keySuffix}`,
                  value: amount,
                  lang: currentLanguage,
                  id: `committed_${record.key}${keySuffix}`,
                  key: `committed_${record.key}${keySuffix}`,
                  displayType:
                    userInfo.canEditDashboard && projectManagement.enabled && record.type === "VALUES"
                      ? "input"
                      : "text",
                  suffix:
                    record.type === "MARGIN_PERCENT" || record.type === "PROGRESS_PERCENT" || record.type === "PERCENT"
                      ? "PERCENT"
                      : amountSuffix,
                  customInput: Input,
                  onChange: (e) => handleChange(e, record, "COMMITTED"),
                })
              ),
          },
          {
            title: t("projectsManagement.dashboard.performance.estimateToComplete"),
            dataIndex: ["projectProgressValues", "ESTIMATE_TO_COMPLETE"],
            width: 150,
            className: "table-value-right toSeeBetter" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) => formatNumberPerformance(amount, record),
          },
          {
            title: t("projectsManagement.dashboard.performance.currentEstimateAtCompletion"),
            dataIndex: ["projectProgressValues", "CURRENT_ESTIMATE_AT_COMPLETION"],
            width: 150,
            className: "table-value-right toSeeBetterHghlighted" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) => formatNumberPerformance(amount, record),
          },
          {
            title: t("projectsManagement.dashboard.performance.previousEstimateAtCompletion"),
            dataIndex: ["projectProgressValues", "PREVIOUS_ESTIMATE_AT_COMPLETION"],
            width: 150,
            className: "table-value-right" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) => formatNumberPerformance(amount, record),
          },
        ],
      },
      {
        title: t("projectsManagement.dashboard.performance.variances"),
        className: isPrint ? "tablePrintLargeFontSize" : "",
        children: [
          {
            title: "EAC | EACm-1",
            dataIndex: ["variancesValues", "EAC_EACM1"],
            width: 150,
            className: "table-value-right toSeeBetterShadow oneline" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) => {
              return (
                record.type !== "PROGRESS_PERCENT" && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {coloredDot(amount, record)}
                    {formatNumberPerformance(amount, record)}
                  </div>
                )
              );
            },
          },
          {
            title: "EAC | BAC",
            dataIndex: ["variancesValues", "EAC_BCU"],
            width: 150,
            className: "table-value-right toSeeBetterShadow oneline" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) => {
              return (
                record.type !== "PROGRESS_PERCENT" && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {coloredDot(amount, record)}
                    {formatNumberPerformance(amount, record)}
                  </div>
                )
              );
            },
          },
          {
            title: "EAC | SCU",
            dataIndex: ["variancesValues", "EAC_SCU"],
            width: 150,
            className: "table-value-right toSeeBetterShadow oneline" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) => {
              return (
                record.type !== "PROGRESS_PERCENT" && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {coloredDot(amount, record)}
                    {formatNumberPerformance(amount, record)}
                  </div>
                )
              );
            },
          },
        ],
      },
      {
        title: t("projectsManagement.dashboard.performance.upselling"),
        className: isPrint ? "tablePrintLargeFontSize" : "",
        children: [
          {
            title: t("projectsManagement.dashboard.performance.changeOrdersToBeRecived"),
            dataIndex: ["upSelling", "CHANGE_ORDERS_TO_BE_RECEIVED"],
            width: 150,
            className: "table-value-right toSeeBetter" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) =>
              isPrint ? (
                <div className="oneline">{formatNumberPerformance(amount, record)}</div>
              ) : (
                formatNumber({
                  decimalScale: editableNumberDecimalScale(
                    record,
                    userInfo.canEditDashboard &&
                      projectManagement.enabled &&
                      (record.type === "REVENUE" || record.type === "VALUES")
                  ),
                  name: `changeOrdersToBeRecived_${record.key}${keySuffix}`,
                  value: amount,
                  lang: currentLanguage,
                  id: `changeOrdersToBeRecived_${record.key}${keySuffix}`,
                  key: `changeOrdersToBeRecived_${record.key}${keySuffix}`,
                  displayType:
                    userInfo.canEditDashboard &&
                    projectManagement.enabled &&
                    (record.type === "REVENUE" || record.type === "VALUES")
                      ? "input"
                      : "text",
                  suffix:
                    record.type === "MARGIN_PERCENT" || record.type === "PROGRESS_PERCENT" || record.type === "PERCENT"
                      ? "PERCENT"
                      : amountSuffix,
                  customInput: Input,
                  onChange: (e) => handleChange(e, record, "CHANGE_ORDERS_TO_BE_RECEIVED"),
                })
              ),
          },
          {
            title: t("projectsManagement.dashboard.performance.adjustedEstimateAtCompletion"),
            dataIndex: ["upSelling", "ADJUSTED_ESTIMATE_AT_COMPLETION"],
            width: 150,
            className: "table-value-right toSeeBetterHghlighted" + (isPrint ? " tablePrintLargeFontSize" : ""),
            render: (amount, record) => formatNumberPerformance(amount, record),
          },
        ],
      },
    ];
  };

  const handleChange = (e, record, column) => {
    if (record.type === "VALUES") {
      customForm[`VALUES-${record.key}`].forEach((v, i) => {
        if (v.column === column) {
          dispatch(
            setValuePerformance(unformatNumber(e.target.value, currentLanguage, "amount"), record.type, record.key, i)
          );
        }
      });
    } else if (record.type === "REVENUE") {
      // I know there is only one revenue
      dispatch(setValuePerformance(unformatNumber(e.target.value, currentLanguage, "amount"), record.type));
    }
  };

  const downloadTable = async () => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const file = await ProjectDashboard.downloadPerformance(params.id);
    if (!file.error) {
      const blob = new Blob([file]);
      exportFile(blob, `Performance-${params.id}.csv`);
      showSuccess();
    } else {
      showError(get(file, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const saveTable = async () => {
    setState((prevState) => ({ ...prevState, saveLoading: true }));
    const saveForm = [];
    Object.keys(customForm).forEach((cf) => {
      if (isArray(customForm[cf])) {
        customForm[cf].forEach((v) => {
          saveForm.push(v);
        });
      } else {
        saveForm.push(customForm[cf]);
      }
    });
    const data = await ProjectDashboard.saveDataPerformance(params.id, saveForm);
    if (!data.error) {
      showSuccess();
      getData();
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, saveLoading: false }));
  };
  const visibleTableRef = useRef(null);
  const hiddenTableRef = useRef(null);

  const handleExport = async () => {
    if (!hiddenTableRef.current) return;

    // Mostra temporaneamente la tabella nascosta
    hiddenTableRef.current.style.visibility = "visible";
    hiddenTableRef.current.style.position = "absolute";
    hiddenTableRef.current.style.top = "0";
    hiddenTableRef.current.style.left = "0";

    // Aggiunge temporaneamente il div al DOM (necessario per html2canvas)
    document.body.appendChild(hiddenTableRef.current);

    // Cattura l'immagine
    const canvas = await html2canvas(hiddenTableRef.current, {
      useCORS: true,
      scale: 2, // Aumenta la qualità
      backgroundColor: "#fff", // Evita trasparenze
    });

    // Ripristina lo stato originale
    document.body.removeChild(hiddenTableRef.current);
    hiddenTableRef.current.style.visibility = "hidden";

    // Scarica il PNG
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "tabella.png";
    link.click();
  };
  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          isLoading={state.loading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={downloadTable}
        />
        <Button
          isDisabled={!state.tableContent || isEmpty(state.tableContent)}
          className="ml-10"
          isLoading={state.loading}
          currentIcon={<PrinterOutlined className="icon-medium" />}
          clickCallback={handleExport}
        />
      </Row>
      <div ref={visibleTableRef} style={{ overflowX: "auto", width: "100%" }}>
        <Table
          bordered
          dataSource={state.tableContent}
          columns={columns()}
          size="small"
          pagination={false}
          scroll={{ x: 1500 }}
          className="breakline"
          rowClassName={(record) => record.type}
        />
      </div>
      <div
        ref={hiddenTableRef}
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          visibility: "hidden",
          backgroundColor: "white", // Evita trasparenze
          padding: "16px", // Dà un po' di spazio ai bordi
        }}
      >
        <p className="tablePrintLargeFontSize">{t("projectsManagement.dashboard.performance.currencyNote")}</p>
        <Table
          bordered
          columns={columns(true)}
          dataSource={state.tableContent}
          pagination={false}
          rowClassName={(record) => record.type}
          scroll={{ x: "max-content" }}
          rowHoverable={false}
          style={{ pointerEvents: "none", whiteSpace: "pre-wrap" }}
        />
      </div>
      <Row type="flex" align="middle" justify="end" className="mt-10">
        <Button
          isDisabled={!(isDirtyCustomForm && userInfo.canEditDashboard && projectManagement.enabled)}
          isLoading={state.saveLoading}
          currentIcon={<SaveOutlined className="icon-medium" />}
          clickCallback={saveTable}
        />
      </Row>
    </>
  );
};

export default Performance;
